import { UserRole } from './user.model'

interface BaseDashboardKpis<Role extends UserRole> {
  role: Role
}

export interface LearnerEnrollmentSummary {
  enrollmentId: string
  courseId: string
  courseTitle: string
  courseCode: string | null
  programmeId: string | null
  enrollmentStatus: string
  enrolledAt: string | null
}

export interface LearnerAssessmentSummary {
  drafts: number
  submitted: number
  graded: number
}

export interface LearnerQuizSummary {
  attempts: number
  avgScore: number
}

export interface LearnerDashboardKpis extends BaseDashboardKpis<UserRole.LEARNER> {
  learnerId: string
  totalCourses: number
  enrollments: LearnerEnrollmentSummary[]
  modulesTotal: number
  modulesCompleted: number
  percentComplete: number
  assignments: LearnerAssessmentSummary
  quizzes: LearnerQuizSummary
  certificatesEarned: number
  lastActiveAt: string | null
}

export interface InstructorCourseSummary {
  courseId: string
  courseTitle: string
  courseCode?: string | null
  enrollmentCount?: number | null
  completionRate?: number | null
  updatedAt?: string | null
}

export interface InstructorSubmissionSummary {
  submissionId: string
  learnerName?: string | null
  courseTitle?: string | null
  submittedAt?: string | null
  status?: string | null
}

export interface InstructorQuizAttemptSummary {
  attemptId: string
  learnerName?: string | null
  quizTitle?: string | null
  score?: number | null
  attemptedAt?: string | null
}

export interface InstructorDashboardKpis extends BaseDashboardKpis<UserRole.INSTRUCTOR> {
  instructorId: string
  coursesCount: number
  courses: InstructorCourseSummary[]
  activeStudents: number
  pendingSubmissions: number
  avgStudentScorePct: number
  recentSubmissions: InstructorSubmissionSummary[]
  recentQuizAttempts: InstructorQuizAttemptSummary[]
  contentUploads: number
  courseCompletionRate: number
}

export interface ProgrammeEnrollmentSummary {
  programmeId: string
  programmeTitle: string
  enrollments: number
}

export interface PaymentsSummary {
  totalAmount: number
  count: number
  period: {
    from: string | null
    to: string | null
  }
}

export interface SystemUsageSummary {
  logins7: number
  logins30: number
}

export interface AdminDashboardKpis extends BaseDashboardKpis<UserRole.ADMIN> {
  usersByRole: Record<string, number>
  activeLearners: number
  totalCourses: number
  activeCourses: number
  totalProgrammes: number
  enrollmentsByProgramme: ProgrammeEnrollmentSummary[]
  applicationsPending: number
  payments: PaymentsSummary
  globalCourseCompletionRate: number
  systemUsage: SystemUsageSummary
}

export type RoleDashboardKpis =
  | LearnerDashboardKpis
  | InstructorDashboardKpis
  | AdminDashboardKpis

export const isLearnerDashboardKpis = (
  data: RoleDashboardKpis | null | undefined,
): data is LearnerDashboardKpis => data?.role === UserRole.LEARNER

export const isInstructorDashboardKpis = (
  data: RoleDashboardKpis | null | undefined,
): data is InstructorDashboardKpis => data?.role === UserRole.INSTRUCTOR

export const isAdminDashboardKpis = (
  data: RoleDashboardKpis | null | undefined,
): data is AdminDashboardKpis => data?.role === UserRole.ADMIN
